<template>
  <v-dialog
    v-model="dialog"
    persistent
    transition="dialog-bottom-transition"
    max-width="1200"
    v-if="isDataLoadedFromServer"
  >
    <div class="bg-white poppins">
      <div class="modal-header align-center">
        <h3 class="mb-0 font-weight-bolder">Edit Courier</h3>
        <div
          class="btn btn-sm btn-icon btn-active-light-info"
          @click="toggleModal"
        >
          <span class="svg-icon">
            <v-icon size="22">mdi-close</v-icon>
          </span>
        </div>
      </div>
      <div class="modal-body d-flex flex-column scroll-y mx-5 mx-xl-15 py-7">
        <!--begin::Body-->
        <div class="row ma-0 pa-0">
          <div class="h-75px col-12 col-sm-6">
            <v-text-field
              v-model="formData.name"
              :label="$t('name')"
              outlined
              dense
              required
              :error-messages="nameErrors"
              @input="$v.formData.name.$touch()"
              @blur="$v.formData.name.$touch()"
            />
          </div>
          <div class="h-75px col-12 col-sm-6">
            <v-text-field
              v-model="formData.system_code"
              :label="$t('system_code')"
              outlined
              dense
              clearable
              :error-messages="system_codeErrors"
              @input="$v.formData.system_code.$touch()"
              @blur="$v.formData.system_code.$touch()"
            />
          </div>
          <div class="h-75px col-12 col-sm-4">
            <v-autocomplete
              dense
              outlined
              v-model="formData.warehouse_id"
              :items="serverData.warehouses"
              item-name="text"
              item-value="index"
              :label="$t('warehouse')"
              clearable
              :error-messages="warehouse_idErrors"
              @input="$v.formData.warehouse_id.$touch()"
              @blur="$v.formData.warehouse_id.$touch()"
            />
          </div>
          <div class="h-75px col-12 col-sm-4">
            <v-autocomplete
              v-model="formData.origin_country_id"
              :label="$t('origin_country_id')"
              item-text="text"
              item-value="index"
              :items="serverData.countries"
              clearable
              outlined
              dense
            />
          </div>
          <div class="h-75px col-12 col-sm-4">
            <v-autocomplete
              v-model="formData.edit_till_status_id"
              :label="$t('edit_till_status')"
              item-text="text"
              item-value="index"
              :items="serverData.edit_till_statuses"
              clearable
              outlined
              dense
            />
          </div>
          <div class="h-75px col-12 col-sm-6">
            <v-file-input
              class="col-12"
              v-model="formData.image"
              show-size
              counter
              multiple
              dense
              outlined
              accept="image/png, image/jpeg, image/bmp"
              prepend-icon="mdi-camera"
              :label="$t('image')"
              clearable
              :error-messages="imageErrors"
              @input="$v.formData.image.$touch()"
              @blur="$v.formData.image.$touch()"
            />
          </div>

          <div class="h-75px col-12 col-sm-6">
            <v-text-field
              v-model="formData.url"
              :label="$t('url')"
              clearable
              outlined
              dense
              :error-messages="urlErrors"
              @input="$v.formData.url.$touch()"
              @blur="$v.formData.url.$touch()"
            />
          </div>
          <div class="h-75px col-12 col-sm-6 col-md-4">
            <v-autocomplete
              dense
              outlined
              v-model="formData.type_id"
              :items="serverData.types"
              item-name="text"
              item-value="index"
              :label="$t('type')"
              clearable
              :error-messages="type_idErrors"
              @input="$v.formData.type_id.$touch()"
              @blur="$v.formData.type_id.$touch()"
            />
          </div>
          <div class="h-75px col-12 col-sm-6 col-md-4">
            <v-text-field
              v-model="formData.api_key"
              :label="$t('api_key')"
              clearable
              outlined
              dense
              @input="$v.formData.api_key.$touch()"
              @blur="$v.formData.api_key.$touch()"
            />
          </div>
          <div class="h-75px col-12 col-sm-6 col-md-4">
            <v-text-field
              v-model="formData.api_secret"
              :label="$t('api_secret')"
              clearable
              outlined
              dense
              @input="$v.formData.api_secret.$touch()"
              @blur="$v.formData.api_secret.$touch()"
            />
          </div>
          <div class="h-75px col-12 col-sm-6 col-md-4">
            <v-text-field
              v-model="formData.webhook_client_secret"
              :label="$t('client_secret')"
              clearable
              outlined
              disabled
              dense
            />
          </div>

          <div class="h-75px col-12 col-sm-6 col-md-4">
            <v-autocomplete
              v-model="formData.awb_create_status"
              :label="$t('awb_create_status')"
              item-text="text"
              item-value="index"
              :items="serverData.order_statuses"
              clearable
              outlined
              dense
            />
          </div>
          <div class="h-75px col-12 col-sm-6 col-md-4">
            <v-autocomplete
              v-model="formData.awb_update_status"
              :label="$t('awb_update_status')"
              item-text="text"
              item-value="index"
              :items="serverData.order_statuses"
              clearable
              outlined
              dense
            />
          </div>
          <div class="h-75px col-12 col-sm-6 col-md-4">
            <v-autocomplete
              v-model="formData.awb_cancel_status"
              :label="$t('awb_cancel_status')"
              item-text="text"
              item-value="index"
              :items="serverData.order_statuses"
              clearable
              outlined
              dense
            />
          </div>

          <div class="h-75px col-12 col-sm-6 col-md-4">
            <v-text-field
              v-model="formData.delivery_no_prefix"
              :label="$t('delivery_no_prefix')"
              clearable
              outlined
              dense
              @input="$v.formData.delivery_no_prefix.$touch()"
              @blur="$v.formData.delivery_no_prefix.$touch()"
            />
          </div>
          <div class="h-75px col-12 col-sm-6 col-md-4">
            <v-text-field
              v-model="formData.delivery_no_length"
              :label="$t('delivery_no_length')"
              clearable
              outlined
              dense
              :error-messages="delivery_no_lengthErrors"
              @input="$v.formData.delivery_no_length.$touch()"
              @blur="$v.formData.delivery_no_length.$touch()"
            />
          </div>
          <div class="h-75px col-12 col-sm-6 col-md-4">
            <v-text-field
              v-model="formData.delivery_no_count"
              :label="$t('delivery_no_count')"
              type="Number"
              clearable
              outlined
              dense
              :error-messages="delivery_no_countErrors"
              @input="$v.formData.delivery_no_count.$touch()"
              @blur="$v.formData.delivery_no_count.$touch()"
              @change="
                () => validateMinValue('delivery_no_count', 0, 'formData')
              "
            />
          </div>
          <div class="h-75px col-12 col-sm-6 col-md-4">
            <v-text-field
              v-model="formData.delivery_no_from"
              :label="$t('delivery_no_from')"
              clearable
              outlined
              dense
              :error-messages="delivery_no_fromErrors"
              @input="$v.formData.delivery_no_from.$touch()"
              @blur="$v.formData.delivery_no_from.$touch()"
            />
          </div>
          <div class="h-75px col-12 col-sm-6 col-md-4">
            <v-text-field
              v-model="formData.delivery_no_to"
              :label="$t('delivery_no_to')"
              clearable
              outlined
              dense
              :error-messages="delivery_no_toErrors"
              @input="$v.formData.delivery_no_to.$touch()"
              @blur="$v.formData.delivery_no_to.$touch()"
            />
          </div>
        </div>
        <!--end::Body-->
        <div class="my-4 d-flex flex-grow-1 align-end justify-center">
          <button
            type="reset"
            class="btn btn-light mr-3 px-5 py-3 ls1"
            @click="resetEditForm"
          >
            Clear
          </button>
          <button
            type="submit"
            class="btn btn-info px-5 py-3 ls1"
            @click="submitEditForm"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";

import { validationMixin } from "vuelidate";
import { required, url, numeric } from "vuelidate/lib/validators";
import fieldValueValidation from "@/own/mixins/fieldValueValidation.js";

export default {
  name: "EditItem",
  mixins: [validationMixin, fieldValueValidation],
  validations: {
    formData: {
      name: { required },
      image: {},
      system_code: { required },
      url: { url },
      delivery_no_prefix: {},
      delivery_no_length: { numeric },
      delivery_no_from: { numeric },
      delivery_no_to: { numeric },
      delivery_no_count: { numeric },
      warehouse_id: { required },
      type_id: { required },
    },
  },
  props: ["refresher", "pageLoader"],
  data: () => ({
    dialog: false,
    serverData: null,
    formData: {
      carrier_id: null,
      image: null,
      system_code: null,
      warehouse_id: null,
      name: null,
      url: null,
      api_key: null,
      api_secret: null,
      delivery_no_prefix: null,
      delivery_no_length: null,
      delivery_no_from: null,
      delivery_no_to: null,
      delivery_no_count: null,
      origin_country_id: null,
      edit_till_status_id: null,
      type_id: null,
      awb_create_status: null,
      awb_update_status: null,
      awb_cancel_status: null,
    },
  }),
  methods: {
    handleFormValidation(fieldName, vueObj) {
      const errors = [];
      if (!vueObj.$v.formData[fieldName].$dirty) return errors;
      if ("required" in vueObj.$v.formData[fieldName]) {
        !vueObj.$v.formData[fieldName].required &&
          errors.push(this.$t("validation_this_field_is_required"));
      }
      if ("url" in vueObj.$v.formData[fieldName]) {
        !vueObj.$v.formData[fieldName].url &&
          errors.push(this.$t("validation_this_url_is_invalid"));
      }
      if ("numeric" in vueObj.$v.formData[fieldName]) {
        !vueObj.$v.formData[fieldName].numeric &&
          errors.push(this.$t("validation_this_must_be_a_number"));
      }
      if ("email" in vueObj.$v.formData[fieldName]) {
        !vueObj.$v.formData[fieldName].email &&
          errors.push(this.$t("validation_this_email_address_is_invalid"));
      }
      return errors;
    },
    loadDataFromServer() {
      this.pageLoader(true);
      let data = { carrier_id: this.$store.getters.getItemForAction.id };
      ApiService.post("/shipping/couriers/edit", data)
        .then((response) => {
          this.serverData = response.data;
          this.formData = { ...this.serverData.carrier };
          this.formData.image = null;
          this.pageLoader(false);
        })
        .catch(() => {
          this.dialog = false;
          this.pageLoader(false);
        });
    },
    convertToFormData() {
      let data = new FormData();
      Object.entries(this.formData).forEach((item) => {
        if (
          (item[1] && item[0] !== "image" && item[0] !== "photo") ||
          item[1] === 0
        ) {
          if (Array.isArray(item[1])) {
            item[1].forEach((it) => data.append(`${item[0]}[]`, it));
          } else {
            data.append(item[0], item[1]);
          }
        } else if (item[1] && (item[0] === "image" || item[0] === "photo")) {
          data.append(item[0], item[1][0]);
        }
      });
      return data;
    },
    submitEditForm() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.pageLoader(true);
        let data = this.convertToFormData();
        ApiService.post("/shipping/couriers/update", data)
          .then(() => {
            Swal.fire({
              title: "Updated",
              text: `Courier data has been updated`,
              icon: "success",
              showConfirmButton: false,
              timer: 2500,
            });
            this.toggleModal();
            this.pageLoader(false);
            this.refresher();
          })
          .catch(() => {
            this.pageLoader(false);
          });
      }
    },
    resetEditForm() {
      this.$v.$reset();
      this.formData = {
        image: null,
        system_code: null,
        name: null,
        url: null,
        api_key: null,
        api_secret: null,
        delivery_no_prefix: null,
        delivery_no_length: null,
        delivery_no_from: null,
        delivery_no_to: null,
        delivery_no_count: null,
        origin_country_id: null,
        edit_till_status_id: null,
        warehouse_id: null,
        type_id: null,
        awb_create_status: null,
        awb_update_status: null,
        awb_cancel_status: null,
      };
    },
    toggleModal() {
      if (this.dialog) this.resetEditForm();
      else this.loadDataFromServer();

      this.dialog = !this.dialog;
    },
  },
  computed: {
    isDataLoadedFromServer: function () {
      return !!this.serverData;
    },
    nameErrors() {
      return this.handleFormValidation("name", this);
    },
    imageErrors() {
      return this.handleFormValidation("image", this);
    },
    system_codeErrors() {
      return this.handleFormValidation("system_code", this);
    },
    urlErrors() {
      return this.handleFormValidation("url", this);
    },
    delivery_no_fromErrors() {
      return this.handleFormValidation("delivery_no_from", this);
    },
    delivery_no_lengthErrors() {
      return this.handleFormValidation("delivery_no_length", this);
    },
    delivery_no_toErrors() {
      return this.handleFormValidation("delivery_no_to", this);
    },
    delivery_no_countErrors() {
      return this.handleFormValidation("delivery_no_count", this);
    },
    warehouse_idErrors() {
      return this.handleFormValidation("warehouse_id", this);
    },
    type_idErrors() {
      return this.handleFormValidation("type_id", this);
    },
  },
};
</script>

<style scoped>
.editorStyle {
  max-height: 70vh;
  overflow: hidden;
}
</style>
